/** BORDURA CAROSABILULUI: linia pe care asfaltul se termină în altceva —
 *  verde, trotuar, pământ. O derivare din document, nu un desen: se reface
 *  odată cu drumurile, rampele și locurile de parcare, deci urmează orice
 *  schimbare a planului de situație (Cătălin, 22.10.2026).
 *
 *  ASFALTUL SE IA ÎMPREUNĂ: drumuri, rampe și locuri de parcare sunt o singură
 *  suprafață pavată. Bordura stă numai pe muchiile ei EXTERIOARE — pe muchia
 *  dintre alee și locul de parcare lipit de ea nu e nimic (accesul rămâne
 *  liber), nici pe racordul dintre două drumuri. Unde drumul iese din lot
 *  (gura de la stradă) nu e bordură: dincolo nu e verdele lotului, e strada.
 *
 *  CURBELE SE URMEAZĂ MUCHIE CU MUCHIE: fiecare inel se eșantionează din `PAS`
 *  în `PAS` metri, cu toate vârfurile lui păstrate, iar o probă pusă la
 *  `PROBA` metri în afara muchiei spune dacă acolo mai e asfalt sau nu. Pe o
 *  racordare arcuită sau pe un S eșantioanele cad pe arc, deci bordura e
 *  rotundă exact cât e drumul.
 *
 *  Fără three.js: aritmetică pe poligoane, testabilă direct. */

import { inInel } from "./vegetatie";

type Inel = number[][];
export interface Pavaj { poly: Inel; holes?: Inel[] }

/** Din cât în cât se probează o muchie. */
export const PAS = 0.25;
/** Cât de departe în afara muchiei se caută asfalt: peste rostul de câțiva cm
 *  dintre alee și locul de parcare, sub lățimea oricărei fâșii de verde. */
export const PROBA = 0.35;
/** O bucată de bordură sub atât nu e bordură, e un rest de eșantionare. */
const MIN_M = 0.6;

const pePavaj = (x: number, y: number, p: Pavaj) =>
  inInel(x, y, p.poly) && !(p.holes ?? []).some((h) => inInel(x, y, h));

const arieSemnata = (r: Inel) => {
  let a = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += r[j][0] * r[i][1] - r[i][0] * r[j][1];
  return a / 2;
};

/** Bordurile: polilinii (în metri) pe muchiile exterioare ale pavajului luat
 *  împreună; `lot` = limita terenului, dincolo de care nu se pune nimic. */
export function borduri(pavaj: readonly Pavaj[], lot: Inel | null = null): number[][][] {
  const pavate = pavaj.filter((p) => p.poly.length >= 3);
  const out: number[][][] = [];
  const afara = (x: number, y: number) =>
    !pavate.some((p) => pePavaj(x, y, p)) && (!lot || lot.length < 3 || inInel(x, y, lot));
  for (const p of pavate) {
    // exteriorul și găurile, fiecare cu normala spre AFARA pavajului
    const inele: { r: Inel; semn: number }[] = [
      { r: p.poly, semn: arieSemnata(p.poly) > 0 ? -1 : 1 },
      ...(p.holes ?? []).filter((h) => h.length >= 3).map((h) => ({ r: h, semn: arieSemnata(h) > 0 ? 1 : -1 })),
    ];
    for (const { r, semn } of inele) {
      // 1. eșantioanele inelului, cu verdictul fiecăruia: e bordură aici?
      const probe: { x: number; y: number; e: boolean }[] = [];
      for (let i = 0; i < r.length; i++) {
        const a = r[i], b = r[(i + 1) % r.length];
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (L < 1e-6) continue;
        const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
        // normala la stânga muchiei e spre interior pe un inel pozitiv; `semn` o întoarce spre afară
        const nx = -uy * semn, ny = ux * semn;
        const n = Math.max(1, Math.ceil(L / PAS));
        for (let k = 0; k < n; k++) {               // vârful de la capăt e primul eșantion al muchiei următoare
          const t = k / n;
          const x = a[0] + ux * L * t, y = a[1] + uy * L * t;
          const e = afara(x + nx * PROBA, y + ny * PROBA)
            && !pavate.some((q) => q !== p && pePavaj(x - nx * 0.02, y - ny * 0.02, q));
          probe.push({ x, y, e });
        }
      }
      if (!probe.length) continue;
      // 2. runurile: inelul e închis, deci se pornește de după un gol (sau, fără
      //    niciun gol, bordura e tot inelul, închis pe el însuși)
      const start = probe.findIndex((q) => !q.e);
      if (start < 0) {
        out.push([...probe.map((q) => [q.x, q.y]), [probe[0].x, probe[0].y]]);
        continue;
      }
      let run: number[][] = [];
      const inchide = () => {
        if (run.length >= 2 && lungime(run) >= MIN_M) out.push(run);
        run = [];
      };
      for (let k = 1; k <= probe.length; k++) {
        const q = probe[(start + k) % probe.length];
        if (q.e) run.push([q.x, q.y]); else inchide();
      }
      inchide();
    }
  }
  return out;
}

function lungime(run: number[][]): number {
  let L = 0;
  for (let i = 1; i < run.length; i++) L += Math.hypot(run[i][0] - run[i - 1][0], run[i][1] - run[i - 1][1]);
  return L;
}
